import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import PrototypeLayout from '../PrototypeLayout';
import { colors, font, type as ty, layout } from '../theme/tokens';
import { getDoorNodes, DoorNode, doorLabel } from '../data/doors';
import { PeriodSelection, defaultPeriod, indicesFor } from '../data/period';
import { cyclesFor } from '../data/maintenance';
import {
  WAVE_POINTS, SAMPLE_MS, PEAK_CEILING, Envelope,
  representativeCycle, cycleEnvelope,
} from '../data/waveform';
import PeriodSwitch from '../components/PeriodSwitch';
import { useFleet } from '../FleetContext';
import { useI18n } from '../i18n';
import { panel, Badge, PageHeader, PanelTitle, DemoFooter } from '../components/ui';

// ── Door nodes and unit identity: see src/data/unit.ts ─────────────────────────────────────────


const maxOf = (doors: DoorNode[]) => Math.max(...doors.map(d => d.cycles), 1);

// Trace colours. Current uses a deepened brand gold so it reads on white;
// position is a muted blue dashed line.
const TRACE_CURRENT = '#B58900';
const TRACE_CURRENT_FILL = 'rgba(255,209,0,0.18)';
const TRACE_POSITION = colors.blue;
// Reference band: the current trace's own hue, well back so the live line wins.
const BAND_FILL = 'rgba(181,137,0,0.13)';
const BAND_EDGE = 'rgba(181,137,0,0.38)';

function cycleColor(cycles: number, maxCycles: number): string {
  const frac = cycles / maxCycles;
  if (frac > 0.85) return colors.alert;
  if (frac > 0.65) return colors.warn;
  if (frac > 0.45) return colors.ok;
  return colors.info;
}

/**
 * Condition badge for a node.
 *
 * Deliberately NOT cycleTone(): that is cycles relative to the busiest door on
 * the unit, so it marks the hardest-working door rather than an unhealthy one
 * and labelled a 92%-health door 'service due' on every vehicle. The dots on
 * the schematic stay on the relative scale — they are a duty map — while this
 * badge reads the door's own condition score.
 */
function healthTone(health: number): 'alert' | 'warn' | 'ok' {
  if (health < 70) return 'alert';
  if (health < 85) return 'warn';
  return 'ok';
}

function healthLabelKey(health: number): string {
  if (health < 70) return 'common.serviceDue';
  if (health < 85) return 'p3.monitorCondition';
  return 'common.nominal';
}


/** Scale a canvas for the device pixel ratio so lines aren't blurry. */
function prepCanvas(canvas: HTMLCanvasElement, cssW: number, cssH: number) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

// ── Train schematic ──────────────────────────────────────────────────────────

const TrainSchematic: React.FC<{
  doors: DoorNode[];
  selectedId: string | null;
  analyzing: string | null;
  onSelect: (id: string) => void;
}> = ({ doors, selectedId, analyzing, onSelect }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const { t } = useI18n();
  const maxCycles = maxOf(doors);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const ns = 'http://www.w3.org/2000/svg';
    const mkEl = (tag: string, attrs: Record<string, string | number>) => {
      const e = document.createElementNS(ns, tag);
      Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, String(v)));
      return e;
    };

    const W = 640, cabW = 36, gap = 6, numCars = 4;
    const bodyW = (W - cabW * 2 - gap * (numCars + 1)) / numCars;
    const bodyH = 130, trainY = 44;
    const midY = trainY + bodyH / 2;
    const carStartX = gap + cabW + gap;
    const rearCabX = carStartX + bodyW * numCars + gap * numCars;
    const doorW = 36, doorH = 18, doorInset = 11, doorMargin = 13;

    const cleanup: Array<() => void> = [];

    [trainY - 16, trainY + bodyH + 16].forEach(tYy => {
      svg.appendChild(mkEl('line', { x1: 8, y1: tYy, x2: 632, y2: tYy, stroke: colors.border, 'stroke-width': 3, 'stroke-linecap': 'round' }));
    });

    [gap, rearCabX].forEach(x => {
      svg.appendChild(mkEl('rect', { x, y: trainY, width: cabW, height: bodyH, rx: 4, fill: colors.bgAlt, stroke: colors.borderStrong, 'stroke-width': 1 }));
      const t = mkEl('text', { x: x + cabW / 2, y: midY, 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': 8, fill: colors.inkFaint, 'font-family': font.mono, 'letter-spacing': '0.08em' });
      t.textContent = 'CAB';
      svg.appendChild(t);
    });

    (['A', 'B', 'C', 'D'] as const).forEach((wagen, wi) => {
      const cx = carStartX + wi * (bodyW + gap);
      svg.appendChild(mkEl('rect', { x: cx, y: trainY, width: bodyW, height: bodyH, rx: 2, fill: colors.bg, stroke: colors.border, 'stroke-width': 1 }));
      svg.appendChild(mkEl('line', { x1: cx + 8, y1: midY, x2: cx + bodyW - 8, y2: midY, stroke: colors.border, 'stroke-width': 0.75, 'stroke-dasharray': '4 3' }));

      const lbl = mkEl('text', { x: cx + bodyW / 2, y: midY, 'text-anchor': 'middle', 'dominant-baseline': 'central', 'font-size': 11, fill: colors.inkGhost, 'font-family': font.mono, 'font-weight': 500, 'letter-spacing': '0.06em' });
      lbl.textContent = `Wagen ${wagen}`;
      svg.appendChild(lbl);

      const doorDefs = [
        { id: `${wagen}1L`, side: 'L', x: cx + doorMargin },
        { id: `${wagen}2L`, side: 'L', x: cx + bodyW - doorMargin - doorW },
        { id: `${wagen}1R`, side: 'R', x: cx + doorMargin },
        { id: `${wagen}2R`, side: 'R', x: cx + bodyW - doorMargin - doorW },
      ];

      doorDefs.forEach(dd => {
        const door = doors.find(d => d.id === dd.id);
        if (!door) return;
        const dy = dd.side === 'L' ? trainY + doorInset : trainY + bodyH - doorInset - doorH;
        const isSelected = selectedId === dd.id;
        const isAnalyzing = analyzing === dd.id;
        const cc = cycleColor(door.cycles, maxCycles);

        const g = mkEl('g', { style: 'cursor:pointer' });

        g.appendChild(mkEl('rect', {
          x: dd.x, y: dy, width: doorW, height: doorH, rx: 2,
          fill: isSelected ? colors.blue : colors.bg,
          stroke: isSelected ? colors.blue : isAnalyzing ? colors.accentDeep : colors.borderStrong,
          'stroke-width': isSelected || isAnalyzing ? 2 : 1,
        }));

        const txt = mkEl('text', {
          x: dd.x + doorW / 2, y: dy + doorH / 2,
          'text-anchor': 'middle', 'dominant-baseline': 'central',
          'font-size': 8, fill: isSelected ? colors.onDark : colors.inkMuted,
          'font-family': font.mono, 'font-weight': 500,
        });
        txt.textContent = doorLabel(dd.id);
        g.appendChild(txt);

        g.appendChild(mkEl('circle', {
          cx: dd.x + doorW - 3.5, cy: dy + 3.5, r: 3.5,
          fill: cc, stroke: colors.bg, 'stroke-width': 1,
        }));

        const onClick = () => onSelect(dd.id);
        g.addEventListener('click', onClick);
        cleanup.push(() => g.removeEventListener('click', onClick));
        svg.appendChild(g);
      });
    });

    return () => { cleanup.forEach(fn => fn()); };
  }, [doors, maxCycles, selectedId, analyzing, onSelect]);

  return (
    <Box>
      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 1 }}>
        {t('p2.sideL')}
      </Typography>
      <svg ref={svgRef} viewBox="0 0 640 218" style={{ display: 'block', width: '100%' }} role="img">
        <title>{t('p3.nodeSelector')}</title>
      </svg>
      <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mt: 1 }}>
        {t('p2.sideR')}
      </Typography>
    </Box>
  );
};

// ── Waveform chart ───────────────────────────────────────────────────────────

const WaveformChart: React.FC<{
  currentData: number[];
  positionData: number[];
  analyzing: boolean;
  standbyLabel: string;
  /** Reference band for this door over the selected period. */
  band: Envelope | null;
}> = ({ currentData, positionData, analyzing, standbyLabel, band }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = 800, H = 320;
    const ctx = prepCanvas(canvas, W, H);
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    const padL = 52, padR = 52, padT = 18, padB = 34;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const points = WAVE_POINTS;
    // A degraded door draws well over 2,000 mA, so a fixed ceiling clipped its
    // trace flat against the top of the plot. Scale to whatever is in view.
    const inView = Math.max(
      2000,
      band ? Math.max(...band.hi) : 0,
      currentData.reduce((a, v) => (v > a ? v : a), 0),
    );
    const maxCurrent = Math.ceil((inView * 1.08) / 500) * 500;
    const c = ctx;

    // Plot area
    c.fillStyle = colors.bg;
    c.fillRect(padL, padT, chartW, chartH);

    // Grid + left axis (current)
    c.lineWidth = 1;
    [0, 0.25, 0.5, 0.75, 1].forEach(frac => {
      const y = padT + chartH - frac * chartH;
      c.strokeStyle = frac === 0 ? colors.borderStrong : colors.border;
      c.beginPath(); c.moveTo(padL, y); c.lineTo(W - padR, y); c.stroke();

      c.fillStyle = colors.inkFaint;
      c.font = `10px ${font.mono}`;
      c.textAlign = 'right';
      c.fillText(String(Math.round(frac * maxCurrent)), padL - 8, y + 3);

      // Right axis (position, 0–255)
      c.textAlign = 'left';
      c.fillText(String(Math.round(frac * 255)), W - padR + 8, y + 3);
    });

    // Axis captions
    c.fillStyle = TRACE_CURRENT;
    c.font = `10px ${font.mono}`;
    c.textAlign = 'left';
    c.fillText('mA', padL - 30, padT - 4);
    c.fillStyle = TRACE_POSITION;
    c.textAlign = 'right';
    c.fillText('pos', W - padR + 26, padT - 4);

    // X axis
    c.fillStyle = colors.inkFaint;
    c.textAlign = 'center';
    [0, 15, 30, 45, 59].forEach(t => {
      const x = padL + (t / (points - 1)) * chartW;
      c.fillText(`${t * SAMPLE_MS}ms`, x, H - 8);
    });

    const drawLine = (data: number[], maxVal: number, color: string, dash: number[] = []) => {
      if (data.length === 0) return;
      c.strokeStyle = color;
      c.lineWidth = 2;
      c.setLineDash(dash);
      c.lineJoin = 'round';
      c.lineCap = 'round';
      c.beginPath();
      data.forEach((v, i) => {
        const x = padL + (i / (points - 1)) * chartW;
        const y = padT + chartH - (Math.min(v, maxVal) / maxVal) * chartH;
        if (i === 0) c.moveTo(x, y); else c.lineTo(x, y);
      });
      c.stroke();
      c.setLineDash([]);
    };

    // Reference band — the spread of this door's other cycles over the
    // selected period. Drawn first so the live trace reads on top of it.
    if (band && band.sampleCount > 0) {
      const yOf = (v: number) => padT + chartH - (Math.min(v, maxCurrent) / maxCurrent) * chartH;
      const xOf = (i: number) => padL + (i / (points - 1)) * chartW;

      c.fillStyle = BAND_FILL;
      c.beginPath();
      band.hi.forEach((v, i) => { const x = xOf(i), y = yOf(v); if (i === 0) c.moveTo(x, y); else c.lineTo(x, y); });
      for (let i = band.lo.length - 1; i >= 0; i -= 1) c.lineTo(xOf(i), yOf(band.lo[i]));
      c.closePath();
      c.fill();

      c.strokeStyle = BAND_EDGE;
      c.lineWidth = 1;
      c.setLineDash([3, 3]);
      [band.hi, band.lo].forEach(series => {
        c.beginPath();
        series.forEach((v, i) => { const x = xOf(i), y = yOf(v); if (i === 0) c.moveTo(x, y); else c.lineTo(x, y); });
        c.stroke();
      });
      c.setLineDash([]);
    }

    // Current fill
    if (currentData.length > 0) {
      c.fillStyle = TRACE_CURRENT_FILL;
      c.beginPath();
      c.moveTo(padL, padT + chartH);
      currentData.forEach((v, i) => {
        const x = padL + (i / (points - 1)) * chartW;
        const y = padT + chartH - (Math.min(v, maxCurrent) / maxCurrent) * chartH;
        c.lineTo(x, y);
      });
      c.lineTo(padL + ((currentData.length - 1) / (points - 1)) * chartW, padT + chartH);
      c.closePath();
      c.fill();
    }

    drawLine(currentData, maxCurrent, TRACE_CURRENT);
    drawLine(positionData, 255, TRACE_POSITION, [5, 5]);

    // Sweep line
    if (analyzing && currentData.length > 0 && currentData.length < points) {
      const x = padL + ((currentData.length - 1) / (points - 1)) * chartW;
      c.strokeStyle = colors.accentDeep;
      c.lineWidth = 1.5;
      c.beginPath(); c.moveTo(x, padT); c.lineTo(x, padT + chartH); c.stroke();
    }

    // Plot border
    c.strokeStyle = colors.border;
    c.lineWidth = 1;
    c.strokeRect(padL, padT, chartW, chartH);

    // Standby
    if (currentData.length === 0 && !analyzing) {
      c.fillStyle = colors.inkGhost;
      c.font = `12px ${font.mono}`;
      c.textAlign = 'center';
      c.fillText(standbyLabel, W / 2, H / 2);
    }
  }, [currentData, positionData, analyzing, standbyLabel, band]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '800 / 320' }}
    />
  );
};

// ── Cycle distribution ───────────────────────────────────────────────────────

const CycleChart: React.FC<{
  doors: DoorNode[];
  selectedId: string | null;
  /** Cycles per door over the selected reporting period, keyed by door id. */
  values: Record<string, number>;
}> = ({ doors, selectedId, values }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const maxCycles = Math.max(...doors.map(d => values[d.id] ?? 0), 1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = 400, H = 220;
    const ctx = prepCanvas(canvas, W, H);
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    const padL = 6, padR = 6, padT = 8, padB = 24;
    const chartH = H - padT - padB;
    const barW = (W - padL - padR) / doors.length;

    // Baseline
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT + chartH);
    ctx.lineTo(W - padR, padT + chartH);
    ctx.stroke();

    doors.forEach((door, i) => {
      const value = values[door.id] ?? 0;
      const barH = (value / maxCycles) * chartH;
      const x = padL + i * barW + barW * 0.15;
      const w = barW * 0.7;
      const y = padT + chartH - barH;
      const isSelected = selectedId === door.id;

      ctx.fillStyle = isSelected ? cycleColor(value, maxCycles) : colors.border;
      ctx.fillRect(x, y, w, barH);

      if (isSelected) {
        ctx.fillStyle = colors.ink;
        ctx.font = `bold 9px ${font.mono}`;
      } else {
        ctx.fillStyle = colors.inkFaint;
        ctx.font = `9px ${font.mono}`;
      }
      ctx.textAlign = 'center';
      ctx.fillText(door.label, x + w / 2, H - 8);
    });
  }, [doors, values, maxCycles, selectedId]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '400 / 220' }}
    />
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const Prototype3: React.FC = () => {
  const { unitId, caption } = useFleet();
  const { t, locale } = useI18n();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState<string | null>(null);
  const [currentData, setCurrentData] = useState<number[]>([]);
  const [positionData, setPositionData] = useState<number[]>([]);
  const [peakCurrent, setPeakCurrent] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [period, setPeriod] = useState<PeriodSelection>(defaultPeriod);
  const doors = useMemo(() => getDoorNodes(unitId), [unitId]);
  // Lifetime cycles drive node colour and the service badge — that is wear.
  const maxCycles = maxOf(doors);

  // Cycles over the selected reporting period, from the same generator the
  // fleet overview and the maintenance tab use, so all three tabs agree.
  const periodCycles = useMemo(() => {
    const out: Record<string, number> = {};
    doors.forEach(d => { out[d.id] = cyclesFor(unitId, d.id, period); });
    return out;
  }, [unitId, doors, period]);
  const periodDays = indicesFor(period).length;

  // Reference band for the selected node over the selected period.
  const envelope = useMemo(
    () => (selectedId ? cycleEnvelope(unitId, selectedId, period) : null),
    [unitId, selectedId, period],
  );
  const selectedDoor = doors.find(d => d.id === selectedId) ?? null;

  const runDiagnostics = useCallback((id: string) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setSelectedId(id);
    setAnalyzing(id);
    setCurrentData([]);
    setPositionData([]);
    setPeakCurrent(null);

    const wf = representativeCycle(unitId, id);
    let i = 0;
    let peak = 0;

    intervalRef.current = setInterval(() => {
      if (i >= wf.current.length) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setAnalyzing(null);
        return;
      }
      const cur = wf.current[i];
      if (cur > peak) { peak = cur; setPeakCurrent(peak); }
      setCurrentData(prev => [...prev, cur]);
      setPositionData(prev => [...prev, wf.position[i]]);
      i++;
    }, 40);
  }, [unitId]);

  // Clear the trace when the unit changes: the old waveform belongs to another
  // vehicle and would silently mislead.
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setSelectedId(null);
    setAnalyzing(null);
    setCurrentData([]);
    setPositionData([]);
    setPeakCurrent(null);
  }, [unitId]);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const isAnalyzing = analyzing !== null;

  return (
    <PrototypeLayout protoId="03" titleKey="proto.03.title">
      <PageHeader
        eyebrow={`${t('p3.system')} · ${caption}`}
        title={t('p3.title')}
        right={
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: colors.ok }} />
              <Typography sx={{ ...ty.meta, color: colors.inkMuted }}>{t('p3.nodesOnline')}</Typography>
            </Box>
            <Badge label={t('common.syntheticData')} t="info" />
          </>
        }
      />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' },
        gap: 3,
        alignItems: 'start',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>

          {/* Node selector */}
          <Box sx={panel}>
            <PanelTitle
              right={
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {[
                    { color: colors.info,  label: t('p3.lowUse') },
                    { color: colors.ok,    label: t('p3.normal') },
                    { color: colors.warn,  label: t('p3.high') },
                    { color: colors.alert, label: t('p3.critical') },
                  ].map(l => (
                    <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: l.color }} />
                      <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkMuted }}>
                        {l.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            >
              {t('p3.nodeSelector')}
            </PanelTitle>
            <TrainSchematic doors={doors} selectedId={selectedId} analyzing={analyzing} onSelect={runDiagnostics} />
          </Box>

          {/* Waveforms */}
          <Box sx={panel}>
            <PanelTitle
              right={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                  {isAnalyzing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{
                        width: 7, height: 7, borderRadius: '50%',
                        backgroundColor: colors.accentPress,
                        animation: 'p3pulse 0.8s infinite',
                        '@keyframes p3pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                      }} />
                      <Typography sx={{ ...ty.meta, color: colors.accentDeep, fontWeight: 700 }}>
                        {t('p3.analysing')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box sx={{ width: 18, height: 3, backgroundColor: TRACE_CURRENT, borderRadius: '2px' }} />
                      <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkMuted }}>
                        {t('p3.current')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box sx={{
                        width: 18, height: 3, borderRadius: '2px',
                        backgroundImage: `repeating-linear-gradient(90deg, ${TRACE_POSITION} 0 4px, transparent 4px 8px)`,
                      }} />
                      <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkMuted }}>
                        {t('p3.position')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
            >
              {t('p3.waveforms')}
            </PanelTitle>

            {/* Provenance: the trace is modelled from the door's condition
                score, not a captured measurement. Say so on the page rather
                than only in conversation. */}
            <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkGhost, mb: 1.5 }}>
              {t('p3.representative')}
            </Typography>

            {selectedDoor && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                <Typography sx={{
                  fontFamily: font.sans, fontSize: '16px', fontWeight: 700,
                  letterSpacing: '-0.01em', color: colors.ink,
                }}>
                  {selectedDoor.label}
                </Typography>
                <Typography sx={{ ...ty.meta, color: colors.inkFaint }}>
                  {selectedDoor.ip} · Pi {selectedDoor.piNr}
                </Typography>
              </Box>
            )}

            <WaveformChart
              currentData={currentData}
              positionData={positionData}
              analyzing={isAnalyzing}
              standbyLabel={t('p3.selectPrompt')}
              band={envelope}
            />

            {envelope && envelope.sampleCount > 0 && (
              <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkGhost, mt: 1.5 }}>
                {t('p3.bandNote', {
                  n: envelope.sampleCount,
                  period: t(`period.${period.key}`).toLowerCase(),
                })}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>

          <Box sx={panel}>
            <PanelTitle right={<Badge label={t(`period.${period.key}`)} t="info" />}>
              {t('p3.cycleDistribution')}
            </PanelTitle>
            <PeriodSwitch
              sel={period}
              onChange={setPeriod}
              note={t('period.note', { days: periodDays })}
            />
            <CycleChart doors={doors} selectedId={selectedId} values={periodCycles} />
          </Box>

          {selectedDoor ? (
            <Box sx={{
              ...panel,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0,
                height: '3px',
                backgroundColor: cycleColor(selectedDoor.cycles, maxCycles),
              },
            }}>
              <PanelTitle
                right={<Badge label={t(healthLabelKey(selectedDoor.health))} t={healthTone(selectedDoor.health)} />}
              >
                {t('p3.selectedNode')}
              </PanelTitle>

              {[
                { label: t('p3.doorId'),    value: selectedDoor.label },
                { label: t('p3.ipAddress'), value: selectedDoor.ip },
                { label: 'Pi №',            value: String(selectedDoor.piNr) },
                { label: t('p3.wagen'),     value: selectedDoor.wagen },
                { label: t('p3.side'),      value: selectedDoor.id.endsWith('L') ? t('common.platform') : t('common.trackside') },
                { label: t('p3.cyclesLifetime'), value: selectedDoor.cycles.toLocaleString(locale) },
                { label: t('p3.cyclesPeriod'), value: (periodCycles[selectedDoor.id] ?? 0).toLocaleString(locale) },
                { label: t('common.health'), value: `${selectedDoor.health}%` },
              ].map(row => (
                <Box key={row.label} sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  py: 1, borderBottom: `1px solid ${colors.border}`,
                }}>
                  <Typography sx={{ fontFamily: font.mono, fontSize: '11px', color: colors.inkFaint }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontFamily: font.mono, fontSize: '12px', fontWeight: 500, color: colors.ink }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}

              {peakCurrent !== null && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 0.75 }}>
                    {t('p3.peakCurrent')}
                  </Typography>
                  <Typography sx={{
                    fontFamily: font.sans, fontSize: '32px', fontWeight: 700,
                    letterSpacing: '-0.02em', lineHeight: 1,
                    color: peakCurrent > PEAK_CEILING ? colors.alert : colors.ink,
                  }}>
                    {Math.round(peakCurrent).toLocaleString(locale)}
                    <Box component="span" sx={{
                      fontFamily: font.mono, fontSize: '12px', fontWeight: 400,
                      color: colors.inkFaint, ml: 1,
                    }}>
                      mA
                    </Box>
                  </Typography>
                  {peakCurrent > PEAK_CEILING && (
                    <Typography sx={{ fontFamily: font.sans, fontSize: '12px', color: colors.alert, mt: 0.75 }}>
                      {t('p3.aboveCeiling', { limit: PEAK_CEILING.toLocaleString(locale) })}
                    </Typography>
                  )}

                  {/* The comparison that turns a trace into a reading: is this
                      cycle inside the door's own recent spread, or above it? */}
                  {envelope && envelope.sampleCount > 0 && (
                    <Box sx={{
                      mt: 1.5, pt: 1.5,
                      borderTop: `1px solid ${colors.border}`,
                    }}>
                      <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.5 }}>
                        {t('p3.vsOwnBand')}
                      </Typography>
                      <Typography sx={{
                        fontFamily: font.sans, fontSize: '13px', fontWeight: 600,
                        color: envelope.percentile >= 95 ? colors.warn : colors.ink,
                      }}>
                        {envelope.percentile >= 95
                          ? t('p3.bandHigh')
                          : envelope.percentile <= 10
                            ? t('p3.bandLow')
                            : t('p3.insideBand')}
                      </Typography>
                      <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 0.5 }}>
                        {t('p3.bandPercentile', { pct: envelope.percentile, n: envelope.sampleCount })}
                      </Typography>
                      <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkGhost, mt: 0.5 }}>
                        {t('p3.bandSelfOnly')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={panel}>
              <PanelTitle>{t('p3.standby')}</PanelTitle>
              <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, lineHeight: 1.6 }}>
                {t('p3.standbyText')}
              </Typography>
            </Box>
          )}

          <Box sx={panel}>
            <PanelTitle>{t('p3.quickSelect')}</PanelTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {doors.map(door => {
                const active = selectedId === door.id;
                return (
                  <Button
                    key={door.id}
                    onClick={() => runDiagnostics(door.id)}
                    disableRipple
                    sx={{
                      fontFamily: font.mono,
                      fontSize: '11px',
                      fontWeight: active ? 700 : 500,
                      px: 1.25,
                      py: 0.5,
                      minWidth: 0,
                      borderRadius: layout.radius,
                      textTransform: 'none',
                      backgroundColor: active ? colors.accent : colors.bg,
                      border: `1px solid ${active ? colors.accent : colors.border}`,
                      color: active ? colors.ink : colors.inkMuted,
                      '&:hover': {
                        backgroundColor: active ? colors.accent : colors.accentWash,
                        borderColor: colors.accent,
                        color: colors.ink,
                      },
                    }}
                  >
                    {door.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      <DemoFooter note={`${t('common.syntheticNote')} · ${t('p3.simulated')}`} />
    </PrototypeLayout>
  );
};

export default Prototype3;
